import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, SortingState } from '@tanstack/react-table';

import DocumentLink from 'components/shared/DocumentLink';
import ControlledTable from 'components/shared/table/ControlledTable';
import useIsMobile from 'hooks/useIsMobile';
import usePagination from 'hooks/usePagination';
import { dateAtTime } from 'lib/date';
import Attachment from 'models/Attachment';
import { attachmentsQueryKeys, indexAttachments } from 'services/api/attachments';
import colors from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';

const columnHelper = createColumnHelper<Attachment>();

const SORT_FIELD_MAP: Record<string, string> = {
  createdAt: 'createdAt',
};

function getColumns() {
  return [
    columnHelper.accessor((row) => row.filename, {
      header: 'File Name',
      id: 'filename',
      cell: (info) => (
        <DocumentLink entry={info.row.original} attachableModel='Review::Admission'>
          <BodySmallBold className='first-column' color={colors.primaryBlue}>
            {info.getValue() || `Untitled ${info.row.original.contentType} ${info.row.index}`}
          </BodySmallBold>
        </DocumentLink>
      ),
    }),
    columnHelper.accessor(
      (row) => ({ userFullName: row.createdByUser?.fullName, groupName: row.createdByGroup?.name }),
      {
        id: 'createdByUser',
        header: 'Uploaded By',
        cell: (info) => {
          const { userFullName, groupName } = info.getValue();
          if (!userFullName) return <div>&mdash;</div>;
          return (
            <div>
              {userFullName} {groupName && `at ${groupName}`}
            </div>
          );
        },
      }
    ),
    columnHelper.accessor((row) => row.createdAt, {
      header: 'Uploaded On',
      id: 'createdAt',
      cell: (info) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>{dateAtTime(info.getValue())}</div>
          </div>
        </div>
      ),
    }),
  ];
}

type Props = {
  reviewId: string;
};

export default function AdmissionFilesTab({ reviewId }: Props) {
  const isMobile = useIsMobile();
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>([{ id: 'createdAt', desc: true }]);

  const sortConfig = sort[0];
  const sortBy = sortConfig
    ? `${SORT_FIELD_MAP[sortConfig.id] ?? sortConfig.id} ${sortConfig.desc ? 'desc' : 'asc'}`
    : undefined;

  const queryParams = useMemo(
    () => ({
      reviewId,
      attachableModel: 'Review::Admission',
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      include: 'blob,extractDataAIJob,createdByUser',
      archived: false,
      sortBy,
    }),
    [reviewId, pagination.paging.page, pagination.paging.pageSize, sortBy]
  );

  const { data, isLoading } = useQuery({
    queryKey: attachmentsQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexAttachments(queryParams, signal),
    enabled: !!reviewId,
  });

  const files = data?.data ?? [];
  const pageCount = data?.meta.totalPages ?? 0;
  const columns = useMemo(() => getColumns(), []);

  return (
    <ControlledTable<Attachment>
      data={files}
      columns={columns}
      loading={isLoading}
      pageCount={pageCount}
      pagination={pagination}
      sorting={{ sortBy: sort, setSortBy: setSort }}
      state={{
        columnVisibility: {
          createdByUser: !isMobile,
          createdAt: !isMobile,
        },
      }}
    />
  );
}
